import { Customer } from '@model/customer';
import { Globals } from '@theme/utils/globals';
import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WalletService } from '@service/wallet/wallet.service';
import { CustomerService } from '@service/customer/customer.service';
import { Wallet, WalletInfo } from '@model/wallet';
import { ToastrService } from 'ngx-toastr';
import { EmitterService } from '@service/emitter/emitter.service';

@Component({
  selector: 'app-wallet-transaction',
  templateUrl: './wallet-transaction.component.html',
  styleUrls: ['./wallet-transaction.component.scss']
})
export class WalletTransactionComponent implements OnInit {
  title: string = "Transaction wallet";
  form: FormGroup;
  submit: boolean = false;
  customer: Customer;
  customerUuid: string;
  customerSelected: any;
  wallet: Wallet;
  walletInfo: WalletInfo;
  loading: boolean = false;
  loadingWallet: boolean = false;
  required = Globals.required;
  global = { country: Globals.country, device: Globals.device };

  typeTransaction = [
    { label: 'Dépôt', value: 'DEPOT', icon: 'fa-arrow-down', class: 'text-success' },
    { label: 'Retrait', value: 'RETRAIT', icon: 'fa-arrow-up', class: 'text-danger' }
  ];

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private walletService: WalletService,
    private customerService: CustomerService,
    private toastr: ToastrService,
    private emitter: EmitterService
  ) {
    this.customer = this.customerService.getCustomer();
    if (this.customer) {
      this.customerUuid = this.customer.uuid;
      this.title = `Transaction wallet - ${this.customer.nom}`;
      this.loadWallet();
    } else {
      this.title = "Transaction wallet";
    }
    this.newForm();
  }

  /**
   * Définir le client sélectionné depuis EntityFinder
   */
  setCustomerUuid(uuid: string): void {
    if (uuid) {
      this.f.customer.setValue(uuid);
      this.loadCustomer(uuid);
    } else {
      this.customer = null;
      this.customerUuid = null;
      this.customerSelected = null;
      this.wallet = null;
      this.f.customer.setValue('');
    }
  }

  /**
   * Charger les données du client
   */
  loadCustomer(uuid: string): void {
    if (!uuid) {
      return;
    }
    
    this.customerService.getSingle(uuid).subscribe(
      (res: Customer) => {
        this.customer = res;
        this.customerUuid = res.uuid;
        this.title = `Transaction wallet - ${res.nom}`;
        this.customerService.setCustomer(res);
        
        // Mettre à jour customerSelected pour EntityFinder
        this.customerSelected = {
          photoSrc: res.photoSrc,
          title: res.nom,
          detail: res.telephone || res.searchableDetail
        };
        
        // Charger le wallet
        this.loadWallet();
      },
      (error) => {
        console.error('Erreur chargement client:', error);
        this.toastr.error('Erreur lors du chargement du client', 'Erreur');
      }
    );
  }

  /**
   * Définir le client sélectionné (depuis le modal si nécessaire)
   */
  setCustomer(customer: Customer): void {
    this.customer = customer;
    this.customerUuid = customer.uuid;
    this.title = `Transaction wallet - ${customer.nom}`;
    this.customerService.setCustomer(customer);
    
    // Mettre à jour le formulaire
    this.f.customer.setValue(customer.uuid);
    
    // Mettre à jour customerSelected
    this.customerSelected = {
      photoSrc: customer.photoSrc,
      title: customer.nom,
      detail: customer.telephone || customer.searchableDetail
    };
    
    this.loadWallet();
  }

  ngOnInit(): void {
  }

  newForm(): void {
    // Si un client est déjà sélectionné, le champ n'est pas requis, sinon il l'est
    const customerValidators = this.customerUuid ? [] : [Validators.required];
    
    this.form = this.formBuilder.group({
      customer: [this.customerUuid || '', customerValidators],
      type: ['DEPOT', [Validators.required]],
      montant: ['', [Validators.required, Validators.min(1)]],
      libelle: ['']
    });
    
    // Initialiser customerSelected si un client est déjà sélectionné
    if (this.customer) {
      this.customerSelected = {
        photoSrc: this.customer.photoSrc,
        title: this.customer.nom,
        detail: this.customer.telephone || this.customer.searchableDetail
      };
    }
  }

  get f() {
    return this.form.controls;
  }

  /**
   * Charger le wallet du client
   */
  loadWallet(): void {
    const uuid = this.customerUuid || this.f.customer?.value;
    if (!uuid) {
      this.wallet = null;
      this.walletInfo = null;
      return;
    }

    this.loadingWallet = true;
    this.walletService.getWallet(uuid).subscribe(
      (response: WalletInfo) => {
        this.walletInfo = response;
        this.wallet = this.mapWalletData(response.wallet);
        this.loadingWallet = false;
      },
      error => {
        console.error('Erreur chargement wallet:', error);
        this.loadingWallet = false;
        this.toastr.error('Erreur lors du chargement du wallet', 'Erreur');
      }
    );
  }

  /**
   * Transforme les données snake_case de l'API en camelCase
   */
  private mapWalletData(rawWallet: any): Wallet {
    return {
      id: rawWallet.id || 0,
      uuid: rawWallet.uuid || '',
      code: rawWallet.code || '',
      solde: rawWallet.solde_actuel || rawWallet.solde || 0,
      detteExigible: rawWallet.dette_exigible || 0,
      detteFuture: rawWallet.dette_future || 0,
      detteTotale: rawWallet.dette_totale || 0,
      creditDisponible: rawWallet.credit_disponible || 0,
      etat: rawWallet.etat || 'EQUILIBRE',
      statutClient: rawWallet.statut_client || rawWallet.statutClient || 'A_JOUR',
      echeancesEnRetard: rawWallet.echeances_en_retard || 0,
      montantEnRetard: rawWallet.montant_en_retard || 0,
      prochaineEcheance: rawWallet.prochaine_echeance || null,
      montantProchaineEcheance: rawWallet.montant_prochaine_echeance || 0,
      messageEtat: rawWallet.message_etat || '',
      peutAcheter: rawWallet.peut_acheter || false,
      niveauRisque: rawWallet.niveau_risque || 'MOYEN',
      dateMiseAJour: rawWallet.date_mise_a_jour || new Date().toISOString()
    };
  }

  /**
   * Vérifier si le retrait est possible
   */
  canWithdraw(montant: number): boolean {
    if (!this.wallet || !montant || montant <= 0) {
      return false;
    }
    const solde = this.wallet.solde || 0;
    return solde >= montant;
  }
  
  /**
   * Vérifier si un client est sélectionné
   */
  hasCustomer(): boolean {
    return !!(this.f.customer.value || this.customerUuid);
  }

  /**
   * Vérifier le montant lors du changement
   */
  onMontantChange(): void {
    const montant = parseFloat(this.f.montant.value);
    const type = this.f.type.value;

    if (type === 'RETRAIT' && montant > 0) {
      if (!this.canWithdraw(montant)) {
        this.f.montant.setErrors({ soldeInsuffisant: true });
      } else {
        if (this.f.montant.hasError('soldeInsuffisant')) {
          delete this.f.montant.errors['soldeInsuffisant'];
          this.f.montant.updateValueAndValidity();
        }
      }
    }
  }

  /**
   * Soumettre le formulaire
   */
  onConfirme(): void {
    this.submit = true;

    // Vérifier que le client est sélectionné
    const customerUuid = this.f.customer.value || this.customerUuid;
    if (!customerUuid) {
      this.f.customer.setErrors({ required: true });
      this.toastr.warning('Veuillez sélectionner un client', 'Champ requis');
      return;
    }

    if (this.form.invalid) {
      return;
    }

    const type = this.f.type.value;
    const montant = parseFloat(this.f.montant.value);
    const libelle = this.f.libelle.value || null;

    // Vérifier le solde pour un retrait
    if (type === 'RETRAIT' && !this.canWithdraw(montant)) {
      Swal.fire({
        icon: 'error',
        title: 'Solde insuffisant',
        text: `Le solde actuel (${this.formatMontant(this.wallet?.solde || 0)}) est insuffisant pour effectuer un retrait de ${this.formatMontant(montant)}.`,
        confirmButtonText: 'OK'
      });
      return;
    }

    // Confirmation
    const typeLabel = type === 'DEPOT' ? 'dépôt' : 'retrait';
    Swal.fire({
      title: `Confirmer le ${typeLabel}`,
      html: `
        <div class="text-left">
          <p><strong>Type :</strong> ${type === 'DEPOT' ? 'Dépôt' : 'Retrait'}</p>
          <p><strong>Montant :</strong> ${this.formatMontant(montant)}</p>
          <p><strong>Solde actuel :</strong> ${this.formatMontant(this.wallet?.solde || 0)}</p>
          <p><strong>Nouveau solde :</strong> ${this.formatMontant(
            type === 'DEPOT' 
              ? (this.wallet?.solde || 0) + montant 
              : (this.wallet?.solde || 0) - montant
          )}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed) {
        this.processTransaction(type, montant, libelle);
      }
    });
  }

  /**
   * Traiter la transaction
   */
  processTransaction(type: 'DEPOT' | 'RETRAIT', montant: number, libelle: string | null): void {
    // Récupérer le customerUuid depuis le formulaire si nécessaire
    const customerUuid = this.f.customer.value || this.customerUuid;
    
    if (!customerUuid) {
      this.toastr.error('Veuillez sélectionner un client', 'Erreur');
      return;
    }
    
    this.loading = true;

    this.walletService.transaction(customerUuid, type, montant, libelle).subscribe(
      (response) => {
        this.loading = false;
        this.toastr.success(
          `${type === 'DEPOT' ? 'Dépôt' : 'Retrait'} effectué avec succès`,
          'Succès'
        );

        // Émettre un événement pour rafraîchir les composants
        this.emitter.emit({
          action: 'WALLET_TRANSACTION',
          payload: response
        });

        // Recharger le wallet pour afficher les nouvelles valeurs
        this.loadWallet();

        // Fermer le modal après un court délai
        setTimeout(() => {
          this.modal.close('success');
        }, 1500);
      },
      (error) => {
        this.loading = false;
        const errorMessage = error.error?.message || error.message || 'Erreur lors de la transaction';
        this.toastr.error(errorMessage, 'Erreur');
      }
    );
  }

  /**
   * Calculer le nouveau solde après la transaction
   */
  getNewSolde(): number {
    if (!this.wallet || !this.f.montant.value) {
      return this.wallet?.solde || 0;
    }
    const montant = parseFloat(this.f.montant.value) || 0;
    const type = this.f.type.value;
    const soldeActuel = this.wallet.solde || 0;
    
    if (type === 'DEPOT') {
      return soldeActuel + montant;
    } else {
      return soldeActuel - montant;
    }
  }

  /**
   * Formater un montant
   */
  formatMontant(montant: number): string {
    return this.walletService.formatMontant(montant);
  }

  /**
   * Fermer le modal
   */
  onClose(): void {
    this.modal.close('cancel');
  }
}
